import { isValidationError } from '_/common/types/errors.types';
import { ALARM_INTEGRATION_TYPES } from '_/constants/common';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useIntegration, useCreateIntegration, useUpdateIntegration } from '_/hooks/useIntegrations';
import { Form, Input, Select, Alert, Modal, Button } from 'antd';
import React, { useEffect, useState, ReactElement } from 'react';

interface EncryptedConfig {
  integrationKey: string;
}

interface Configuration {
  contactList?: string[];
}

interface IntegrationValues {
  name: string;
  type: string;
  contactList: string[];
  encryptedConfig: EncryptedConfig;
  configuration?: Configuration;
}

interface NewIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  integrationId?: string;
}

function NewIntegration({ open, onClose, integrationId }: NewIntegrationModalProps): ReactElement {
  const { selectedOrgSlug } = useOrganization();
  const [form] = Form.useForm();

  // React Query hooks
  const { data: selectedIntegration } = useIntegration(selectedOrgSlug!, integrationId);
  const {
    mutate: createIntegrationMutation,
    isError: isCreateError,
    error: createError,
  } = useCreateIntegration(selectedOrgSlug!);
  const {
    mutate: updateIntegrationMutation,
    isError: isUpdateError,
    error: updateError,
  } = useUpdateIntegration(selectedOrgSlug!);

  const [isEditMode, setEditMode] = useState(false);
  const [values, setValues] = useState<IntegrationValues>({
    name: '',
    type: 'EMAIL',
    contactList: [],
    encryptedConfig: {
      integrationKey: '',
    },
  });

  useEffect(() => {
    if (integrationId && selectedIntegration) {
      const formValues = {
        type: selectedIntegration.type,
        name: selectedIntegration.name,
        contactList: selectedIntegration.type === 'EMAIL' ? selectedIntegration.configuration?.contactList || [] : [],
        encryptedConfig: { integrationKey: '' },
      };
      setValues(formValues);
      form.setFieldsValue({
        name: formValues.name,
        type: formValues.type,
      });
      setEditMode(true);
    } else if (!selectedIntegration && !isEditMode) {
      setEditMode(false);
      form.resetFields();
    }
  }, [integrationId, selectedIntegration, form, isEditMode]);

  const onContactListChange = (chipsList: string[]) => {
    setValues({
      ...values,
      contactList: chipsList,
    });
  };

  const onEncryptedConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      encryptedConfig: {
        integrationKey: e.target.value,
      },
    });
  };

  const onValuesChange = (changedValues: Partial<IntegrationValues>) => {
    if ('type' in changedValues && changedValues.type) {
      setValues((prev) => ({
        // @ts-ignore
        type: changedValues.type,
        ...prev,
        encryptedConfig: { integrationKey: '' },
      }));
    }
  };

  async function onSaveClick() {
    try {
      await form.validateFields();
      const formValues = form.getFieldsValue();

      // Validate contact list for EMAIL type
      if (formValues.type === 'EMAIL' && (!values.contactList || values.contactList.length === 0)) {
        return; // Don't submit if contact list is empty for EMAIL type
      }

      const configuration = formValues.type === 'EMAIL' ? { contactList: values.contactList } : {};

      if (isEditMode && selectedIntegration) {
        const updatedIntegration = {
          ...(selectedIntegration.name !== formValues.name && { name: formValues.name }),
          ...(selectedIntegration.type !== formValues.type && { type: formValues.type }),
          ...(values.encryptedConfig.integrationKey && { encryptedConfig: values.encryptedConfig }),
          ...(JSON.stringify(selectedIntegration.configuration) !== JSON.stringify(configuration) && {
            configuration: configuration,
          }),
        };

        updateIntegrationMutation(
          {
            integrationId: selectedIntegration.id,
            updates: updatedIntegration,
          },
          {
            onSuccess: () => {
              onClose();
              form.resetFields();
            },
          },
        );
      } else {
        const integration = {
          name: formValues.name,
          type: formValues.type,
          configuration: configuration,
          encryptedConfig: formValues.type === 'PAGERDUTY' ? values.encryptedConfig : undefined,
        };

        createIntegrationMutation(integration, {
          onSuccess: () => {
            onClose();
            form.resetFields();
          },
        });
      }
    } catch (error) {
      // Form validation failed
      console.error('Form validation failed:', error);
    }
  }

  // Extract error messages using type-safe approach
  const saveError = isCreateError ? createError : isUpdateError ? updateError : null;

  const integrationApiErrors = saveError && isValidationError(saveError) ? saveError.getErrorsByType('api') : [];

  const integrationNameErrors = saveError && isValidationError(saveError) ? saveError.getErrorsByType('name') : [];

  // Render configuration type section
  const renderConfigurationType = () => {
    return (
      <div>
        <div style={{ margin: '20px 0 5px 0' }}>
          <span style={{ fontWeight: 'bold' }}>Configuration Information:</span>
        </div>
        {values.type === 'EMAIL' ? (
          <div style={{ marginTop: 10 }}>
            <label
              style={{
                fontSize: 13,
                color: 'rgba(0, 0, 0, 0.6)',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Contact List <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Type and press Enter to add contacts"
              value={values.contactList || []}
              onChange={(chips) => onContactListChange(chips)}
              tokenSeparators={[',']}
              status={!values.contactList?.length ? 'error' : undefined}
            />
            {!values.contactList?.length && (
              <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>Contact list is required</div>
            )}
          </div>
        ) : values.type === 'PAGERDUTY' ? (
          <div style={{ marginTop: 10 }}>
            <label
              style={{
                fontSize: 13,
                color: 'rgba(0, 0, 0, 0.6)',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Pagerduty Integration Key
            </label>
            <Input
              id="integrationKey"
              name="integrationKey"
              placeholder="Enter Pagerduty Integration Key"
              onChange={onEncryptedConfigChange}
              value={values.encryptedConfig?.integrationKey || ''}
            />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Integration' : 'New Integration'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        initialValues={{
          name: '',
          type: 'EMAIL',
        }}
      >
        {integrationApiErrors.length > 0 && (
          <Alert
            message="API Errors"
            description={
              <ul>
                {integrationApiErrors.map((error: string, idx: number) => (
                  <li key={`api-error-${idx}`}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, message: 'Name is required' },
            { whitespace: true, message: 'Name cannot contain only whitespace' },
          ]}
          validateStatus={integrationNameErrors.length > 0 ? 'error' : undefined}
          help={integrationNameErrors.length > 0 ? integrationNameErrors.join(', ') : undefined}
        >
          <Input placeholder="Integration name" />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type is required' }]}>
          <Select
            options={ALARM_INTEGRATION_TYPES.map((option) => ({
              label: option,
              value: option,
              key: option,
            }))}
          />
        </Form.Item>

        {renderConfigurationType()}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: 24 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={onSaveClick}>
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default NewIntegration;
